import * as React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth';
import { useGetGateway } from 'src/features/gateway/api/useGetGateway';
import DeleteGatewayFormModal from 'src/features/gateway/components/form/DeleteGatewayFormModal';
import UpdateGatewayFormModal from 'src/features/gateway/components/form/UpdateGatewayFormModal';
import GatewayPricingPlans from 'src/features/gateway/components/GatewayPricingPlans';
import { GATEWAY_IDENTIFIER_TYPE_MAP } from 'src/features/gateway/utils/constants';
import { gatewayMessages } from 'src/features/gateway/utils/messages';
import BackButton from 'src/features/navigation/components/BackButton';
import { PATH } from 'src/features/navigation/utils/constants';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { VISITOR_CATEGORIES_TRANSLATION_MAP } from 'src/features/zone/utils/constants';
import { zoneMessages } from 'src/features/zone/utils/messages';
import { VISITOR_CATEGORY_TYPE } from 'src/features/zone/utils/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import GeneralBlockAttribute from 'src/registry/components/blocks/GeneralBlockAttribute';
import { SectionBlock, SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import { Button } from 'src/registry/ui/Button';
import { Separator } from 'src/registry/ui/Separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/registry/ui/Tabs';
import { useTitleStore } from 'src/stores/useTitleStore';
import { BOOLEAN_MAP, ICON_MAP } from 'src/utils/constants';
import { commonMessages, generalMessages } from 'src/utils/messages';

export default function GatewayDashboard() {
    const { formatMessage, locale } = useIntl();
    const { gatewayId = '' } = useParams();
    const { parkingId } = useParking();

    const { data: gatewayData } = useGetGateway({ gatewayId, parkingId });

    const accessZones = useHasPermissions([PERMISSION.ACCESS_ZONES]);
    const managePricingPlans = useHasPermissions([PERMISSION.MANAGE_PRICING_PLANS]);
    const manageGateway = useHasPermissions([PERMISSION.MANAGE_GATEWAYS]);

    const { setTitle, removeTitle } = useTitleStore();
    React.useEffect(() => {
        setTitle(formatMessage(gatewayMessages.gatewayDashboard));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    return (
        <>
            <BackButton customRoute={`/${PATH.ADMINISTRATION}/gateway`} />

            <SectionBlock>
                <div className="flex flex-row items-end justify-between p-0">
                    <span className="flex flex-row items-center">
                        <SectionBlockTitle className="mr-2">
                            {gatewayData?.translations.find((translation) => translation.language === locale)?.name ??
                                ''}
                        </SectionBlockTitle>
                    </span>

                    {manageGateway && gatewayData && (
                        <div className="flex flex-row gap-2">
                            <UpdateGatewayFormModal
                                selectedGateway={gatewayData}
                                semantics={{ entryName: formatMessage(gatewayMessages.gateway).toLowerCase() }}
                            >
                                <Button size="mdIcon" variant="outline" className="cursor-pointer">
                                    <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                    <span className="hidden md:block">{formatMessage(commonMessages.edit)}</span>{' '}
                                </Button>
                            </UpdateGatewayFormModal>
                            <DeleteGatewayFormModal
                                selectedGateway={gatewayData}
                                semantics={{ entryName: formatMessage(gatewayMessages.gateway).toLowerCase() }}
                            >
                                <Button size="mdIcon" variant="destructiveLight" className="cursor-pointer">
                                    <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                    <span className="hidden md:block">{formatMessage(generalMessages.delete)}</span>
                                </Button>
                            </DeleteGatewayFormModal>
                        </div>
                    )}
                </div>
            </SectionBlock>

            <GeneralBlock>
                <div className="grid flex-3 grid-cols-3 gap-4">
                    {gatewayData?.translations.map((translation) => {
                        const languageToUpper = translation.language.toUpperCase();
                        return (
                            <GeneralBlockAttribute
                                key={languageToUpper}
                                label={`${formatMessage(generalMessages.name)} (${languageToUpper})`}
                                value={translation.name}
                            />
                        );
                    })}
                </div>
                <Separator />
                <div className="grid flex-3 grid-cols-3 gap-4">
                    {gatewayData?.entry_zone ? (
                        gatewayData.entry_zone.translations.map((translation) => {
                            const languageToUpper = translation.language.toUpperCase();
                            return (
                                <GeneralBlockAttribute
                                    key={languageToUpper}
                                    label={`${formatMessage(gatewayMessages.entryZone)} (${languageToUpper})`}
                                    value={translation.name}
                                    link={
                                        accessZones
                                            ? `/${PATH.ADMINISTRATION}/zone/${gatewayData.entry_zone.id}`
                                            : undefined
                                    }
                                />
                            );
                        })
                    ) : (
                        <GeneralBlockAttribute label={formatMessage(gatewayMessages.entryZone)} />
                    )}
                    {gatewayData?.exit_zone ? (
                        gatewayData.exit_zone.translations.map((translation) => {
                            const languageToUpper = translation.language.toUpperCase();
                            return (
                                <GeneralBlockAttribute
                                    key={languageToUpper}
                                    label={`${formatMessage(gatewayMessages.exitZone)} (${languageToUpper})`}
                                    value={translation.name}
                                    link={
                                        accessZones
                                            ? `/${PATH.ADMINISTRATION}/zone/${gatewayData.exit_zone.id}`
                                            : undefined
                                    }
                                />
                            );
                        })
                    ) : (
                        <GeneralBlockAttribute label={formatMessage(gatewayMessages.exitZone)} />
                    )}
                    <GeneralBlockAttribute
                        label={formatMessage(gatewayMessages.requiredIdentifiers)}
                        value={gatewayData?.required_identifier_types
                            .map((type) => formatMessage(GATEWAY_IDENTIFIER_TYPE_MAP[type]))
                            .join(', ')}
                    />
                    <GeneralBlockAttribute
                        label={formatMessage(gatewayMessages.allowUnknownMainIdentifier)}
                        value={
                            gatewayData?.allow_unknown_main_identifier !== undefined
                                ? formatMessage(
                                      BOOLEAN_MAP[
                                          String(gatewayData.allow_unknown_main_identifier) as 'true' | 'false'
                                      ],
                                  )
                                : '-'
                        }
                    />
                    <GeneralBlockAttribute
                        label={formatMessage(gatewayMessages.requireAllIdentifier)}
                        value={
                            gatewayData?.require_all_identifier !== undefined
                                ? formatMessage(
                                      BOOLEAN_MAP[String(gatewayData.require_all_identifier) as 'true' | 'false'],
                                  )
                                : '-'
                        }
                    />
                    <GeneralBlockAttribute
                        label={formatMessage(zoneMessages.visitorCategories)}
                        value={
                            gatewayData?.visitor_categories?.length
                                ? gatewayData?.visitor_categories
                                      ?.map((g) =>
                                          formatMessage(
                                              VISITOR_CATEGORIES_TRANSLATION_MAP[g as VISITOR_CATEGORY_TYPE] ?? {
                                                  id: 'common.unknown',
                                                  defaultMessage: g,
                                              },
                                          ),
                                      )
                                      .join(', ')
                                : '-'
                        }
                    />
                </div>
                <Separator />
                <div className="grid flex-3 grid-cols-3 gap-4">
                    {gatewayData?.translations.map((translation) => {
                        const languageToUpper = translation.language.toUpperCase();
                        return (
                            <GeneralBlockAttribute
                                label={`${formatMessage(generalMessages.description)} (${languageToUpper})`}
                                value={translation.description?.length ? translation.description : '-'}
                                key={translation.id}
                            />
                        );
                    })}
                </div>
            </GeneralBlock>
            {managePricingPlans && (
                <Tabs defaultValue="pricing-plans">
                    <TabsList>
                        <TabsTrigger value="pricing-plans">
                            <FormattedMessage
                                defaultMessage={pricingPlanMessages.pricingPlans.defaultMessage}
                                id={pricingPlanMessages.pricingPlans.id}
                            />
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value="pricing-plans">
                        <GatewayPricingPlans gatewayId={gatewayId} />
                    </TabsContent>
                </Tabs>
            )}
        </>
    );
}
